import Button from '@rio-cloud/rio-uikit/Button';
import SimpleTooltip from '@rio-cloud/rio-uikit/components/tooltip/SimpleTooltip';
import LoadMoreButton from '@rio-cloud/rio-uikit/LoadMoreButton';
import NoData from '@rio-cloud/rio-uikit/NoData';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import Notification from '@rio-cloud/rio-uikit/Notification';
import TableCol from '@rio-cloud/rio-uikit/TableCol';
import TableHead from '@rio-cloud/rio-uikit/TableHead';
import TableSettingsDialog from '@rio-cloud/rio-uikit/TableSettingsDialog';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import useSorting from '@rio-cloud/rio-uikit/useSorting';
import useTableSelection from '@rio-cloud/rio-uikit/useTableSelection';
import SortDirection, { SortDirectionType } from '@rio-cloud/rio-uikit/utils/SortUtils';
import { PLACEMENT } from '@rio-cloud/rio-uikit/values/Placement';
import isEmpty from 'lodash/fp/isEmpty';
import omit from 'lodash/fp/omit';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import SearchField from '@/components/atom/SearchField/SearchField';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { useSaveUserKPIDataVisualisationPreferenceMutation } from '@/services/chatApi/KPIDataVisualisationPreferenceApi/KPIDataVisualisationPreferenceApi';
import { TDataVisualization } from '@/services/threadApi/types';
import { selectMessageById } from '@/store/chat/chat.slice';
import {
  initKPIDataVisualization,
  selectIsKPIDirty,
  selectKPIDataVisualization,
  updateKPIDataVisualization,
} from '@/store/chat/KPIDataVisualizationPreference/KPIDataVisualizationPreference.slice';
import { TKPIDataVisualizationPrefence } from '@/store/chat/KPIDataVisualizationPreference/types';
import Logger from '@/utils/logger';
import { isSortDirection } from '@/utils/sortHelper/sortHelper';

import { TABLE_TEST_IDS, TABLE_TOOLBAR_SEARCH_CLASS } from './constants';
import { TTableProps, TTableSettingsDialogDraft } from './types';
import { SavePreferencesButton } from './UIKitTable.style';
import { useTableIntl } from './useTableIntl';

const LOGGER_TAG = 'UI - UiKitTable';

// INFO: Used to preserve server data order at initial render
const SYTENTHIC_KEY = '__serverOrder';

const DEFAULT_NUMBER_OF_RECORDS = 10; // INFO: Number of table records to be shown

const UiKitTable = <T extends Record<string, unknown>>({ data, messageId }: TTableProps<T>) => {
  const intl = useIntl();
  const {
    searchInTable,
    nothingFound,
    noData,
    refineSearch,
    loadMoreMessage,
    noMoreMessage,
    saveVisualizationPreferences,
    saveVisualizationPreferencesSuccess,
    dialogTitle,
    dialogCloseButtonText,
    dialogResetButtonText,
    dialogSearchPlaceholder,
    dialogNotFoundMessage,
    tableClasses,
  } = useTableIntl(intl);

  const message = useAppSelector(selectMessageById(messageId));
  const isDirty = useAppSelector(selectIsKPIDirty(message.messageId));
  const kpiVisualization = useAppSelector(selectKPIDataVisualization(message.messageId)); // INFO: Source of truth for data visualization information on the UI

  const dispatch = useAppDispatch();

  const [savePreference] = useSaveUserKPIDataVisualisationPreferenceMutation();

  const tableData = useMemo(() => {
    if (!data) return [];
    const arr = Array.isArray(data) ? data : [data];

    return arr.map((row, index) => ({
      [SYTENTHIC_KEY]: index,
      ...row,
    }));
  }, [data]);

  if (tableData.length === 0) return null;

  const defaultColumns = Object.keys(tableData[0]).filter((col) => col !== SYTENTHIC_KEY);
  const columnLabels = Object.fromEntries(defaultColumns.map((col) => [col, col]));

  // INFO: Used as a draft for the setting's dialog column data to avoid persisting on store with every change which would cause multiple re-renders and hide the dialog
  const [dialogDraft, setDialogDraft] = useState<TTableSettingsDialogDraft | null>(null);

  useEffect(() => {
    if (kpiVisualization) return; // INFO: Sets the store as the source of truth of data for the UI.

    // INFO: Default data visualization settings to be used when it is null on the store
    const defaultSettings = { column_orderby: '', hidden_columns: [], column_ordder: defaultColumns };

    const dataVisualization: TDataVisualization = message?.data_visualization?.settings
      ? {
          ...message?.data_visualization,
          settings: {
            column_orderby: message?.data_visualization?.settings.column_orderby ?? defaultSettings.column_orderby,
            hidden_columns: message?.data_visualization?.settings.hidden_columns ?? defaultSettings.hidden_columns,
            column_order: defaultColumns,
          },
        }
      : {
          default_data_visualization: 'table',
          x_axis: '',
          settings: defaultSettings,
        };

    dispatch(
      initKPIDataVisualization({
        messageId: message.messageId,
        data: dataVisualization,
      }),
    );
  }, [message.messageId]);

  const derivedInitialSettings: TKPIDataVisualizationPrefence = useMemo(() => {
    // INFO: Guard cases where kpiVisualization is null (selector not ready yet), it will use data from the message or default visualization

    const tableSettings = message?.data_visualization?.settings;

    let result: TDataVisualization = {
      default_data_visualization: 'table',
      x_axis: message?.data_visualization?.x_axis ?? '',
      settings: {
        column_orderby: '',
        hidden_columns: [''],
        column_order: defaultColumns,
      },
    };

    if (tableSettings) {
      result = {
        ...result,
        settings: {
          column_orderby: tableSettings?.column_orderby ?? (result.settings?.column_orderby || ''),
          hidden_columns: tableSettings.hidden_columns ?? (result.settings?.hidden_columns || ['']),
          column_order: tableSettings?.column_order ?? (result.settings?.column_order || defaultColumns),
        },
      };
    }

    return {
      savedData: result,
      data: result,
      isDirty: false,
    };
  }, [message]);

  const { data: kpiVisualizationData } = kpiVisualization ?? derivedInitialSettings;
  const [rawKey, rawDirection] = kpiVisualizationData!.settings!.column_orderby.split('|');

  const sortKey = rawKey.trim() || undefined;
  const sortDirection =
    rawDirection && isSortDirection(rawDirection?.trim()) ? (rawDirection.trim() as SortDirectionType) : undefined;

  const hiddenColumns = kpiVisualizationData.settings!.hidden_columns;
  const columnOrder = kpiVisualizationData.settings?.column_order ?? defaultColumns;

  const [searchValue, setSearchValue] = useState('');
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [visibleCount, setVisibleCount] = useState(DEFAULT_NUMBER_OF_RECORDS);

  const { sortedItems, setSortKey, setSortDirection } = useSorting<T>(tableData, SYTENTHIC_KEY as keyof T);

  useEffect(() => {
    setSortKey(sortKey as keyof T);
    setSortDirection(sortDirection as unknown as SortDirectionType);
  }, [kpiVisualizationData.settings?.column_orderby]);

  const effectiveHiddenColumns = dialogDraft?.hiddenColumns ?? hiddenColumns;
  const effectiveColumnOrder = dialogDraft?.columnOrder ?? columnOrder;

  const { tableRef, updateRowSelection } = useTableSelection<T>({
    tableData,
    idKey: SYTENTHIC_KEY as keyof T,
  });

  useEffect(() => {
    updateRowSelection();
  }, [searchValue, tableData, updateRowSelection]);

  useEffect(() => {
    // INFO: Updates the store with the draft from the columns change
    if (!showSettingsDialog && dialogDraft) {
      dispatch(
        updateKPIDataVisualization({
          messageId: message.messageId,
          changes: {
            ...kpiVisualization.data,
            settings: {
              ...kpiVisualization.data?.settings,
              column_orderby: kpiVisualization.data.settings?.column_orderby ?? '',
              hidden_columns: dialogDraft.hiddenColumns,
              column_order: dialogDraft.columnOrder,
            },
          },
        }),
      );
      setDialogDraft(null);
    }
  }, [showSettingsDialog]);

  const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
    const sortBy = event.currentTarget.getAttribute('data-sortby');
    let nextDirection = sortDirection;

    if (!sortBy) return;

    if (!sortDirection) nextDirection = SortDirection.ASCENDING;

    if (sortBy === sortKey) {
      // INFO: Saves the new direction and toggles it on the next dispatch action, synchronizing UI and store
      nextDirection = sortDirection === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
    } else {
      nextDirection = SortDirection.ASCENDING;
    }
    dispatch(
      updateKPIDataVisualization({
        messageId: message.messageId,
        changes: {
          ...kpiVisualization.data,
          settings: {
            ...kpiVisualization.data?.settings,
            column_orderby: `${sortBy} | ${nextDirection}`,
            hidden_columns: effectiveHiddenColumns,
          },
        },
      }),
    );
  };

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setVisibleCount(DEFAULT_NUMBER_OF_RECORDS);
  };

  const visibleColumns = useMemo(
    () => effectiveColumnOrder.filter((col) => !effectiveHiddenColumns.includes(col)),
    [effectiveColumnOrder, effectiveHiddenColumns],
  );

  const visibleData = useMemo(() => {
    const removeHidden = omit(effectiveHiddenColumns);
    return sortedItems.map((item) => removeHidden(item));
  }, [sortedItems, effectiveHiddenColumns]);

  const filteredRows = useMemo(() => {
    if (!searchValue) return visibleData;
    const query = searchValue.toLowerCase();
    return visibleData.filter((row) =>
      visibleColumns.some((col) => {
        const value = row[col];
        return value != null && value.toString().toLowerCase().includes(query);
      }),
    );
  }, [searchValue, visibleData, visibleColumns]);

  const displayedRows = filteredRows.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 5, filteredRows.length));
  };

  const handleColumnChange = (newColumnOrder: string[], newHiddenColumns: string[]) => {
    /**
     * INFO: Update the draft state to avoid updating the store which would
     * cause a re-initialization of the settings dialog internal state(causing it to close on column).
     * The draft data is updated to the store when the dialog gets closed where a re-initialisation is already expected
     */
    setDialogDraft({
      columnOrder: newColumnOrder,
      hiddenColumns: newHiddenColumns,
    });
  };

  const handleSaveKPIDataVisualisationPreference = async () => {
    try {
      const settings = kpiVisualization.data.settings;

      const response = await savePreference({
        columnOrderby: settings?.column_orderby ?? '',
        hiddenColumns: settings?.hidden_columns || [],
        messageId,
      });

      if (!response.error) {
        Notification.success(saveVisualizationPreferencesSuccess);
      }
    } catch (error) {
      Logger.error(LOGGER_TAG, 'Failed to save preferences', error);
      // No-opt
    }
  };

  const openSettingsDialog = () => {
    setDialogDraft({
      columnOrder,
      hiddenColumns,
    });
    setShowSettingsDialog(true);
  };

  const closeSettingsDialog = () => {
    setShowSettingsDialog(false);
  };

  return (
    <div className="margin-top-20">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <SearchField
                value={searchValue}
                placeholder={searchInTable}
                testId={TABLE_TEST_IDS.search}
                // INFO: rio-uikit's own toolbar search class carries the 300px width and the
                // full-width behaviour below 480px, so the toolbar keeps owning its geometry
                className={TABLE_TOOLBAR_SEARCH_CLASS}
                // INFO: The filtering is local to the rows already on screen, so it applies as the
                // user types rather than after the debounce a server-side search would need
                shouldUseDebounce={false}
                onSearch={handleSearchValueChange}
              />
            </div>
            <div className="table-toolbar-column">
              <Button
                data-testid={TABLE_TEST_IDS.settings}
                iconOnly
                iconName="icon-settings"
                onClick={openSettingsDialog}
              />
            </div>

            {message?.derivesFromPromptLibrary && (
              <div className="table-toolbar-column">
                <SimpleTooltip allowOnTouch content={saveVisualizationPreferences} placement={PLACEMENT.TOP}>
                  <SavePreferencesButton
                    data-testid={TABLE_TEST_IDS.savePreferences}
                    disabled={!isDirty}
                    iconOnly
                    iconName="icon-save"
                    onClick={handleSaveKPIDataVisualisationPreference}
                  />
                </SimpleTooltip>
              </div>
            )}
          </div>
        </div>
        {showSettingsDialog && (
          <TableSettingsDialog
            show={showSettingsDialog}
            title={dialogTitle}
            onHide={closeSettingsDialog}
            onColumnChange={handleColumnChange}
            defaultColumnOrder={defaultColumns}
            defaultHiddenColumns={[]}
            columnOrder={dialogDraft?.columnOrder ?? effectiveColumnOrder}
            hiddenColumns={dialogDraft?.hiddenColumns ?? effectiveHiddenColumns}
            columnLabels={columnLabels}
            // INFO: First column will not be allowed to be disabled
            disabledColumns={[defaultColumns[0]]}
            closeButtonText={dialogCloseButtonText}
            resetButtonText={dialogResetButtonText}
            searchPlaceholder={dialogSearchPlaceholder}
            notFoundMessage={dialogNotFoundMessage}
            autoLabel="Auto"
            immediateChange
          />
        )}
      </TableToolbar>

      <div className="table-responsive">
        <table ref={tableRef as React.RefObject<HTMLTableElement>} className={tableClasses}>
          <colgroup>
            {visibleColumns.map((col) => (
              <TableCol key={col} />
            ))}
          </colgroup>

          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col}
                  label={col}
                  column={col}
                  sortBy={sortKey}
                  sortDir={sortDirection}
                  onClick={handleSortChange}
                />
              ))}
            </tr>
          </thead>

          <tbody>
            {isEmpty(displayedRows) ? (
              <tr>
                <td colSpan={visibleColumns.length}>
                  <NotFoundState outerClassName="border-none" headline={nothingFound} message={refineSearch} />
                </td>
              </tr>
            ) : (
              displayedRows.map((row, index) => {
                const rowKey = (row.id ?? row.vehicleId ?? index) as string | number;
                return (
                  <tr key={rowKey} data-id={rowKey}>
                    {visibleColumns.map((col) => (
                      <td key={col} data-field={col}>
                        <span>
                          {row[col] != null && row[col] !== '' ? (
                            typeof row[col] === 'object' ? (
                              JSON.stringify(row[col])
                            ) : (
                              String(row[col])
                            )
                          ) : (
                            <NoData text={noData} />
                          )}
                        </span>
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <LoadMoreButton
        loaded={displayedRows.length}
        total={filteredRows.length}
        onLoadMore={handleLoadMore}
        loadMoreMessage={loadMoreMessage}
        noMoreMessage={noMoreMessage}
      />
    </div>
  );
};

export default UiKitTable;
